<template>
	<div>
		<ListLayout
			:title="$t('admin_sidebar_imports_label')"
			:buttonText="$t('admin_imports_newimport_button')"
			@click="showNewImport = true"
		>
			<template #search>
				<div></div>
			</template>
			<template #table>
				<Table
					:columns="columns"
					:rows="importBatches"
					:loadingMore="loadingMore"
					:loadingOverlay="showLoading"
					infinite
					clickable
					loadMoreTrigger="0px"
					emptyState="No imports in history"
					@load-more="loadMore"
					ref="table"
					height="calc(100vh - 145px)"
					@row-click="showImport"
				>
					<template #cell-batch_id="{ data }">
						{{ data.id }}
					</template>
					<template #cell-imported_by="{ data }">
						{{ data.user.fullname }}
					</template>
					<template #cell-created_at="{ data }">
						{{ $m(data.created_at).format('lll') }}
					</template>
					<template #cell-imported_from="{ data }">
						{{
							data.source === 'api'
								? $t('admin_imports_imported_from_api')
								: $t('admin_imports_imported_from_org')
						}}
					</template>
					<template #cell-status="{ data }">
						<component
							:is="getBatchIcon(data.status)"
							:class="getBatchIconClass(data.status)"
							class="status-icon"
							type="regular"
						/>
						{{ $t(batchStatus(data.status)) }}
					</template>
					<template #cell-details="{ data }">
						<div class="details">
							<Button
								link
								v-if="data.status !== 'pending' && data.status !== 'importing'"
								@click="showImport(data)"
								>{{ $t('admin_imports_view_details') }}</Button
							>
							<div v-if="data.status === 'importing'" class="progress">
								<div class="progress-bar">
									<div
										class="progress-bar-fill"
										:style="{ width: data.progress + '%' }"
									></div>
								</div>
								<div class="percentage">{{ `${data.progress}%` }}</div>
							</div>
						</div>
					</template>
					<template #empty>
						<div class="empty-state">
							<FilesUploadIcon />
							<div class="empty-title">
								{{ $t('admin_imports_empty_title') }}
							</div>
							<div class="help-links">
								<Button link @click="showNewImport = true">{{
									$t('admin_imports_start_new_import')
								}}</Button>
								<div
									v-html="
										$t('admin_imports_empty_link', {
											LearnMoreUrl:
												'https://developers.pronto.io/docs/pronto-csv-imports',
										})
									"
								></div>
							</div>
						</div>
					</template>
				</Table>
			</template>
		</ListLayout>
		<NewImport v-if="showNewImport" @cancel="showNewImport = false">
		</NewImport>
		<ImportDetails
			v-if="showImportDetails"
			@cancel="showImportDetails = false"
			:batchdata="batch"
		/>
	</div>
</template>

<script>
import { Table, Button } from '@/components/common'
import { ListLayout, get } from '../common'
import {
	FilesUploadIcon,
	TimerIcon,
	ArrowUpFromArcIcon,
	CircleCheckIcon,
	CircleExclamationIcon,
	CircleVIcon,
	CircleXIcon,
} from '@/icons'
import NewImport from './NewImport'
import ImportDetails from './ImportDetails'
import { getOrganization } from '@/store'
import { bindAppEvents, initPusher } from '@/helpers/pusher'

export default {
	name: 'Imports',
	components: {
		Table,
		ListLayout,
		Button,
		FilesUploadIcon,
		TimerIcon,
		ArrowUpFromArcIcon,
		CircleCheckIcon,
		CircleExclamationIcon,
		NewImport,
		ImportDetails,
		CircleVIcon,
		CircleXIcon,
	},
	data() {
		return {
			importBatches: [],
			loadingMore: false,
			showLoading: false,
			columns: [
				{
					field: 'batch_id',
					label: this.$t('admin_imports_table_heading_batchid'),
					width: '86px',
					minWidth: '86px',
				},
				{
					field: 'imported_by',
					label: this.$t('admin_imports_table_heading_importedby'),
				},
				{
					field: 'created_at',
					label: this.$t('admin_reports_table_heading_date'),
				},
				{
					field: 'imported_from',
					label: this.$t('admin_imports_table_heading_importedfrom'),
				},
				{
					field: 'status',
					label: this.$t('admin_reports_table_heading_status'),
				},
				{
					field: 'details',
				},
			],
			showNewImport: false,
			showImportDetails: false,
			batch: null,
			apiResponse: null,
		}
	},

	async mounted() {
		if (this.$route.params.id && !this.batch) {
			this.batch = { id: this.$route.params.id }
			this.showImportDetails = true
		}
		this.connectPusher()

		this.showLoading = true
		try {
			this.apiResponse = await get('/organization/import_batches')
			this.importBatches = this.apiResponse.data
			this.showLoading = false
		} catch (error) {
			this.showLoading = false
			this.$alert.error(this.$t('common_api_error_save'))
		}
	},

	methods: {
		connectPusher() {
			initPusher()
			const importsChannel = this.$pusher.subscribe(
				`private-organization-imports.${getOrganization().id}`
			)

			//unbind any orphaned events
			importsChannel.unbind()

			importsChannel.bind('pusher:subscription_error', () => {
				setTimeout(this.connectPusher, 5000)
			})

			bindAppEvents({
				ImportbatchAdded: this.importBatchHandler.bind(this),
				ImportbatchUpdated: this.importBatchHandler.bind(this),
			})
		},

		onLoadMore() {
			return
		},

		close() {
			this.showNewImport = false
		},

		showImport(batch) {
			this.batch = batch
			this.showImportDetails = true
		},

		batchStatus(status) {
			return `admin_imports_batch_status_${status}`
		},

		importBatchHandler({ importbatch }) {
			const index = this.importBatches.findIndex(
				(batch) => batch.id === importbatch.id
			)
			if (index !== -1) {
				this.importBatches = this.importBatches.map((batch) =>
					batch.id === importbatch.id ? importbatch : batch
				)
			} else {
				this.importBatches.unshift(importbatch)
			}
		},

		getBatchIcon(status) {
			switch (status) {
				case 'pending':
					return 'TimerIcon'
				case 'validating':
					return 'CircleVIcon'
				case 'importing':
					return 'ArrowUpFromArcIcon'
				case 'completed':
					return 'CircleCheckIcon'
				case 'completed_with_errors':
				case 'failed':
					return 'CircleExclamationIcon'
				case 'canceling':
				case 'canceled':
					return 'CircleXIcon'
				default:
					return null
			}
		},

		getBatchIconClass(status) {
			switch (status) {
				case 'pending':
					return 'pending-icon'
				case 'validating':
					return 'validating-icon'
				case 'importing':
					return 'importing-icon'
				case 'completed':
					return 'completed-icon'
				case 'completed_with_errors':
					return 'completed-errors-icon'
				case 'failed':
					return 'failed-icon'
				case 'canceling':
					return 'canceling-icon'
				case 'canceled':
					return 'cancelled-icon'
				default:
					return null
			}
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			try {
				this.loadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.importBatches = this.importBatches.concat(this.apiResponse.data)
			} catch (e) {
				console.error(e)
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.loadingMore = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
:deep(.list-layout) {
	header {
		height: 105px;
	}

	.empty-state {
		display: flex;
		align-items: center;
		flex-direction: column;

		& > *:not(:last-child) {
			margin-bottom: 20px;
		}

		.empty-title {
			font-size: 24px;
		}
		.help-links {
			display: flex;
			align-items: center;
			a {
				color: var(--blue-300);
				text-decoration: none;
				font-size: 20px;
				&:hover {
					background: transparent;
					color: var(--blue-200);
				}
			}

			& .pronto-btn {
				font-size: 20px;
				padding: 5px;
			}
		}
	}

	.failed-icon,
	.cancelled-icon {
		color: var(--red-400);
	}

	.canceling-icon,
	.completed-errors-icon {
		color: var(--yellow-300);
	}

	.importing-icon,
	.validating-icon {
		color: var(--blue-300);
	}

	.pending-icon {
		color: var(--neutral-400);
	}

	.completed-icon {
		color: var(--teal-300);
	}

	.status-icon {
		vertical-align: top;
		font-size: 19px;
		margin-right: 7px;
	}

	.details {
		display: flex;
		justify-content: flex-end;
		.progress {
			display: flex;
			align-items: center;
			padding-right: 16px;
		}
		.progress-bar {
			height: 5px;
			width: 57px;
			background-color: var(--neutral-200);
			border-radius: 20px;
		}
		.progress-bar-fill {
			height: 100%;
			background-color: var(--blue-300);
			border-radius: 20px;
		}
		.percentage {
			font-family: Avenir-Roman;
			margin-left: 8px;
		}
	}
}
</style>
